import { Evaluation, Oprf, VOPRFClient } from '@cloudflare/voprf-ts';
import { getOprfPublicKey, requestBlindedPepper } from 'services/PepperSignerService';

const SUITE = Oprf.Suite.P384_SHA384;

// P384 compressed-point size (SEC1) and DLEQ proof size (2 * 48-byte scalars) - fixed by the
// suite above. Used below to bridge the two libraries' wire formats (see bridging comment).
const P384_POINT_SIZE = 49;
const P384_PROOF_SIZE = 96;

const clientsByUserId = new Map<string, Promise<VOPRFClient>>();

const toBase64 = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes));
const fromBase64 = (b64: string): Uint8Array => Uint8Array.from(atob(b64), c => c.charCodeAt(0));

/** Each identity has its own OPRF key; its public key is fetched once and the client reused. */
const getVoprfClient = (userId: string): Promise<VOPRFClient> => {
  let client = clientsByUserId.get(userId);
  if (!client) {
    client = getOprfPublicKey(userId)
      .then(publicKeyB64 => new VOPRFClient(SUITE, fromBase64(publicKeyB64)))
      .catch(error => {
        clientsByUserId.delete(userId);
        throw error;
      });
    clientsByUserId.set(userId, client);
  }
  return client;
};

/**
 * @cloudflare/voprf-ts batches inputs, so `EvaluationRequest.serialize()`/`Evaluation.serialize()`
 * both prefix a 2-byte element count and (for Evaluation) a 1-byte mode - neither of which the
 * `voprf` Python package's single-point wire format has. Since this protocol only ever sends one
 * point at a time, convert between the two formats explicitly rather than relying on the
 * (incompatible) raw bytes from either library.
 */
const toSinglePointWire = (evalReqBytes: Uint8Array): Uint8Array => evalReqBytes.slice(2);

const fromSinglePointWire = (pythonBytes: Uint8Array): Uint8Array => {
  // Verified empirically against the `voprf` python package: VerifiableOutput.serialize()
  // is [proof(96 bytes)][point(49 bytes)], not point-then-proof.
  const proof = pythonBytes.slice(0, P384_PROOF_SIZE);
  const point = pythonBytes.slice(P384_PROOF_SIZE, P384_PROOF_SIZE + P384_POINT_SIZE);
  return new Uint8Array([0, 1, ...point, Oprf.Mode.VOPRF, ...proof]);
};

/**
 * Runs the full VOPRF round-trip against savi-pin-oprf: blind `input` client-side, send only
 * the blinded value to the signer, and unblind (finalize) the result - the signer never sees
 * `input` (see docs/auth/savi-connect/pin-auth-services.md). `input` is the Argon2id-derived
 * PIN key, not the raw PIN.
 */
export const derivePepper = async (userId: string, input: Uint8Array): Promise<Uint8Array> => {
  const client = await getVoprfClient(userId);

  const [finData, evalReq] = await client.blind([input]);
  const blindedPepperB64 = await requestBlindedPepper(userId, toBase64(toSinglePointWire(evalReq.serialize())));

  const evaluation = Evaluation.deserialize(SUITE, fromSinglePointWire(fromBase64(blindedPepperB64)));
  const [pepper] = await client.finalize(finData, evaluation);
  return pepper;
};
